import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi } from "@/lib/api/admin";
import { useToast } from "@/hooks/useToast";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function InviteUserModal({ open, onClose }: Props) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState<{ code: string; email: string } | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createInvitation = useMutation({
    mutationFn: () => adminApi.createInvitation({ email }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
      // CreateInvitationResponse models the API's `{ data: invitation }` envelope.
      const { data } = response;
      setSuccessData({ code: data.code, email });
      setEmail("");
      setError("");
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email) {
      setError("Email is required");
      return;
    }
    createInvitation.mutate();
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    setSuccessData(null);
    onClose();
  };

  const getActivationUrl = () => {
    if (!successData) return "";
    const baseUrl = window.location.origin;
    return `${baseUrl}/activate?email=${encodeURIComponent(successData.email)}&code=${successData.code}`;
  };

  const copyToClipboard = async () => {
    try {
      // navigator.clipboard is absent outside a secure context (plain HTTP on a
      // non-localhost origin), so probe before touching .writeText.
      const clipboard = navigator.clipboard as Clipboard | undefined;
      if (typeof clipboard?.writeText !== "function") {
        throw new Error("Clipboard API unavailable");
      }
      await clipboard.writeText(getActivationUrl());
      toast("Activation link copied to clipboard");
    } catch {
      toast(
        "Couldn't copy the activation link. Select the link above and copy it manually."
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite New User</DialogTitle>
          <DialogDescription>
            Send an invitation to a new user to join the platform.
          </DialogDescription>
        </DialogHeader>

        {successData ? (
          <div className="space-y-4">
            <div className="rounded-md bg-green-900/20 p-4">
              <p className="text-sm text-green-200">
                Invitation created successfully!
              </p>
            </div>
            <div className="space-y-2">
              <Label>Activation Link</Label>
              <div className="flex gap-2">
                <Input value={getActivationUrl()} readOnly className="font-mono text-xs" />
                <Button
                  variant="outline"
                  onClick={() => {
                    void copyToClipboard();
                  }}
                >
                  Copy
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Share this link with the user to complete registration.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createInvitation.isPending}>
                {createInvitation.isPending ? "Creating..." : "Create Invitation"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
